const translations = require('../translations');
const orderSummary = require('./orderSummary');
const constants = require('../utils/constants');
const { shouldTriggerGiftVoucherVerifyWithConfig } = require('./discountVoucherVerify');

/** Longueur max du code (injectée par Neos). @returns {number|null} */
const getCodeLength = () => {
    const raw = document.getElementById('gift-voucher-code-length')?.dataset.codeLength;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Seuil pour déclencher verify (smoke : data-min-length, ex. 16 ; sinon codeLength bon).
 * @returns {number|null}
 */
const getMinVerifyLength = () => {
    const el = document.getElementById('gift-voucher-code-length');
    const raw = el?.dataset.minLength || el?.dataset.codeLength;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Préfixe des bons de test admin (Settings adminTestCodePrefix), ex. TEST-.
 * @returns {string|null}
 */
const getTestCodePrefix = () => {
    const raw = document.getElementById('gift-voucher-code-length')?.dataset.testCodePrefix || '';
    const prefix = String(raw).trim().toUpperCase();
    return prefix !== '' ? prefix : null;
};

/**
 * Déclenche l’appel /verify-gift-voucher-code ? (lit data-* DOM).
 *
 * @param {string} rawValue
 * @returns {boolean}
 */
const shouldTriggerGiftVoucherVerify = (rawValue) => {
    return shouldTriggerGiftVoucherVerifyWithConfig(
        rawValue,
        getMinVerifyLength(),
        getTestCodePrefix()
    );
};

const getHiddenGiftVoucherCodeInput = () => document.getElementById('booking-form-giftVoucherCode');

/**
 * Toujours synchroniser le champ visible → hidden (soumission serveur smoke/bon).
 * Particularité : ne pas vider le hidden tant que l’utilisateur tape (seuil verify).
 */
const syncHiddenGiftVoucherCode = (visibleInput) => {
    const hidden = getHiddenGiftVoucherCodeInput();
    if (!hidden) {
        return;
    }
    const value = (visibleInput.value || '').trim();
    hidden.value = value;
};

const buildDiscountVoucherField = () => {
    const codeLength = getCodeLength();
    const hiddenGiftVoucherCodeInput = getHiddenGiftVoucherCodeInput();

    const discountVoucherField = document.createElement('div');
    discountVoucherField.classList.add('tc-website-discountvoucher', 'clearfix');

    const discountVoucherLabel = document.createElement('label');
    discountVoucherLabel.classList.add('form-label');
    discountVoucherLabel.setAttribute('for', 'discount-voucher');
    discountVoucherLabel.innerHTML = translations.DISCOUNT_VOUCHER.label;

    const discountVoucherInput = document.createElement('div');
    discountVoucherInput.classList.add('input');

    const discountVoucherInputField = document.createElement('input');
    discountVoucherInputField.classList.add('form-control');
    discountVoucherInputField.setAttribute('id', 'discount-voucher');
    discountVoucherInputField.setAttribute('type', 'text');
    discountVoucherInputField.setAttribute('placeholder', translations.DISCOUNT_VOUCHER.placeholder);
    // Smoke : éviter uppercase CSS qui trompe la relecture (valeur reste telle quelle)
    discountVoucherInputField.setAttribute('autocapitalize', 'off');
    discountVoucherInputField.setAttribute('spellcheck', 'false');
    if (codeLength !== null) {
        discountVoucherInputField.setAttribute('maxlength', String(codeLength));
    }

    if (constants.existingReservation && constants.reservation.giftVoucherCode && hiddenGiftVoucherCodeInput) {
        discountVoucherInputField.value = hiddenGiftVoucherCodeInput.value = constants.reservation.giftVoucherCode;
    }

    discountVoucherInputField.addEventListener('input', () => {
        checkVoucherCode(discountVoucherInputField);
    })

    const discountVoucherMessage = document.createElement('div');
    discountVoucherMessage.setAttribute('id', 'discount-voucher-message');

    discountVoucherInput.appendChild(discountVoucherInputField);
    discountVoucherField.appendChild(discountVoucherLabel);
    discountVoucherField.appendChild(discountVoucherInput);
    discountVoucherField.appendChild(discountVoucherMessage);

    return discountVoucherField;
}

const checkVoucherCode = (discountVoucherInputField) => {
    const discountVoucherMessage = document.getElementById('discount-voucher-message');
    discountVoucherMessage.classList.remove('visible');

    // Toujours envoyer le code au serveur à la soumission (smoke virtuel)
    syncHiddenGiftVoucherCode(discountVoucherInputField);

    if (!shouldTriggerGiftVoucherVerify(discountVoucherInputField.value)) {
        discountVoucherInputField.setCustomValidity('');
        discountVoucherInputField.classList.remove('is-valid');
        discountVoucherInputField.classList.remove('is-invalid');

        if (document.querySelector('.tc-website-ordersummary')?.querySelector('.discount-row')) {
            orderSummary.removeGiftVoucherDiscount();
        }
        return;
    }

    fetch('/verify-gift-voucher-code', {
        method: 'POST',
        body: JSON.stringify({
            giftVoucherCode: discountVoucherInputField.value,
            locale: document.documentElement.getAttribute('lang'),
            // Smoke 6.7 : allowlist e-mail + total après promo pour forcer 1 CHF
            email: document.querySelector('#booking-form-email')?.value
                || document.querySelector('input[name*="[email]"]')?.value
                || '',
            currentTotal: orderSummary.getCurrentTotal()
        }),
        headers: {
            "Content-type": "application/json; charset=UTF-8"
        }
    })
        .then((response) => response.json())
        .then((response) => {
            let hasDiscount = document.querySelector('.tc-website-ordersummary').querySelector('.discount-row');
            if (response.status === 'valid') {
                discountVoucherInputField.setCustomValidity('');
                discountVoucherInputField.classList.remove('is-invalid');
                discountVoucherInputField.classList.add('is-valid');

                if (!hasDiscount) {
                    orderSummary.addGiftVoucherDiscount(parseFloat(response.availableAmount));
                } else {
                    orderSummary.removeGiftVoucherDiscount();
                    orderSummary.addGiftVoucherDiscount(parseFloat(response.availableAmount));
                }
                syncHiddenGiftVoucherCode(discountVoucherInputField);
            } else {
                discountVoucherInputField.setCustomValidity('Invalid field.');
                discountVoucherInputField.classList.remove('is-valid');
                discountVoucherInputField.classList.add('is-invalid');

                if (hasDiscount) {
                    orderSummary.removeGiftVoucherDiscount();
                }
                // Garder le hidden synchronisé : le serveur tranche à la création
                syncHiddenGiftVoucherCode(discountVoucherInputField);

                if (response.status === 'used' || response.status === 'invalid') {
                    discountVoucherMessage.innerText = response.message;
                    discountVoucherMessage.classList.add('visible');
                }
            }
        })
        .catch((error) => {
            console.error(error);
        });
}

module.exports = {
    buildDiscountVoucherField,
    shouldTriggerGiftVoucherVerify,
    shouldTriggerGiftVoucherVerifyWithConfig,
};
module.exports.default = {
    buildDiscountVoucherField,
    shouldTriggerGiftVoucherVerify,
    shouldTriggerGiftVoucherVerifyWithConfig,
};
